import { Component, OnInit } from '@angular/core';
import { MatTableDataSource, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatNoDataRow } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { ApplicationNote } from 'src/app/models/application_note.model';
import { ApplicationReview } from 'src/app/models/application_review.model';
import { FinancialDocument } from 'src/app/models/financial_document.model';
import { ApplicationEmployeeService } from 'src/app/services/application_employee_service.service';
import { ApplicationNoteService } from 'src/app/services/application_note_service.service';
import { ApplicationService } from 'src/app/services/application_service.service';
import { FinancialDocumentService } from 'src/app/services/financial_document_service.service';
import { TokenStorageService } from 'src/app/services/token_storage.service';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, MatExpansionPanelDescription } from '@angular/material/expansion';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormField, MatLabel, MatInput } from '@angular/material/input';
import { DatePipe } from '@angular/common';

enum ApprovalStatuses {
    Approved = 'Approved',
    Denied = 'Denied',
    NeedsWork = 'Started',
}

@Component({
    selector: 'review-application',
    templateUrl: './review_application.component.html',
    styleUrls: ['./review_application.component.scss'],
    imports: [MatAccordion, MatExpansionPanel, MatProgressBar, MatIconModule, MatExpansionPanelHeader, MatExpansionPanelTitle, MatExpansionPanelDescription, MatCheckboxModule, FormsModule, MatButtonModule, MatTooltipModule, MatFormField, MatLabel, MatInput, MatButton, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatNoDataRow, DatePipe]
})
export class ReviewApplicationComponent implements OnInit {

    application?: ApplicationReview;

    loadingApplication = false;
    loadingNotes = false;
    demographicsValid = false;
    financialsValid = false;
    serviceTypesValid = false;
    saving = false;

    note = '';
    applicationNotes: ApplicationNote[] = [];
    applicationNoteDataSource = new MatTableDataSource<ApplicationNote>([]);
    applicationNoteColumnHeaders: string[] = ['name', 'employee', 'date', 'action'];

    // Share your story
    shareYourStory = '';

    filingTypes = new Map([
        ['UNMARRIED_INDIVIDUAL', 'Unmarried Individual'],
        ['HEAD_OF_HOUSEHOLD', 'Head of Household'],
        ['MARRIED_FILING_JOINTLY', 'Married Filing Jointly'],
    ]);

    salaryTypes = new Map([
        ['YEARLY', 'Yearly Income'],
        ['NINETY_DAYS', '90 Day Income'],
        ['MONTHLY', '30 Day Income'],
        ['BI_WEEKLY', 'Bi-weekly Income'],
        ['WEEKLY', 'Weekly Income']
    ]);

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private applicationService: ApplicationService,
        private financialDocumentService: FinancialDocumentService,
        private tokenStorageService: TokenStorageService,
        private applicationEmployeeService: ApplicationEmployeeService,
        private applicationNoteService: ApplicationNoteService,
    ) {}

    ngOnInit(): void {
        this.activatedRoute.paramMap.subscribe(params => {
            this.loadingApplication = true;
            const id = params.get('id');

            if (id != null) {
                this.applicationService.get([id, 'Review'], new Map()).subscribe((application: any) => {
                    this.application = application;
                    this.getNotes();
                    this.loadingApplication = false;
                    this.shareYourStory = application.shareYourStory;
                });
            } else {
                this.router.navigate(['application']);
            }
        });
    }

    get getDemographicReviewStatus(): string {
        return this.demographicsValid ? 'Reviewed' : 'Unreviewed';
    }

    get getFinancialsReviewStatus(): string {
        return this.financialsValid ?  'Reviewed' : 'Unreviewed';
    }

    get getServiceTypesReviewStatus(): string {
        return this.serviceTypesValid ?  'Reviewed' : 'Unreviewed';
    }

    get getFilingType(): string | undefined {
        return this.application != null ? this.filingTypes.get(this.application.taxFilingType) : '';
    }

    get getSalaryType(): string | undefined {
        return this.application != null ? this.salaryTypes.get(this.application.salaryType) : '';
    }

    downloadFile(financialDocument: FinancialDocument): void {
        if (financialDocument?.financialDocumentId != null) {
            this.financialDocumentService.download(financialDocument.financialDocumentId).subscribe((x: any) => {
                const binaryString = window.atob(x.data);
                const len = binaryString.length;
                const bytes = new Uint8Array(len);

                for (let i = 0; i < len; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }

                const a = window.document.createElement('a');
                a.href = window.URL.createObjectURL(new Blob([bytes], { type: financialDocument.contentType }));
                a.download = financialDocument.name;

                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            });
        }
    }

    onDenyApplication(): void {
        const user = this.tokenStorageService.getUser();

        const employeeId = user.id;
        const applicationId = this.application?.applicationId;
        const approvalStatus = ApprovalStatuses.Denied.toString();

        if (user != null && this.application?.applicationId != null) {
            this.saving = true;

            this.applicationEmployeeService
                .post([], { employeeId, applicationId, approvalStatus })
                .subscribe(x => {
                        this.saving = false;
                        this.router.navigate(['application']);
                    }, (_) => this.saving = false
                );
        }
    }

    onApproveApplication(): void {
        const user = this.tokenStorageService.getUser();

        const employeeId = user.id;
        const applicationId = this.application?.applicationId;
        const approvalStatus = ApprovalStatuses.Approved.toString();

        if (user != null && this.application?.applicationId != null) {
            this.saving = true;

            this.applicationEmployeeService
                .post([], { employeeId, applicationId, approvalStatus })
                .subscribe(x => {
                        this.saving = false;
                        this.router.navigate(['application']);
                    }, (_) => this.saving = false
                );
        }
    }

    onNeedsWorkApplication(): void {
        const user = this.tokenStorageService.getUser();

        const employeeId = user.id;
        const applicationId = this.application?.applicationId;
        const approvalStatus = ApprovalStatuses.NeedsWork.toString();

        if (user != null && this.application?.applicationId != null) {
            this.saving = true;

            this.applicationEmployeeService
                .post([], { employeeId, applicationId, approvalStatus })
                .subscribe(x => {
                        this.saving = false;
                        this.router.navigate(['application']);
                    }, (_) => this.saving = false
                );
        }
    }

    postNote(): void {
        const user = this.tokenStorageService.getUser();

        const employeeId = user.id;
        const applicationId = this.application?.applicationId;
        const note = this.note;

        if (user != null && this.application?.applicationId != null) {
            this.applicationNoteService
                .post([], { employeeId, applicationId, note })
                .subscribe(_ => {
                    this.getNotes();
                    this.note = '';
                }
            );
        }
    }

    getNotes(): void {
        this.loadingNotes = true;
        if (this.application?.applicationId != null) {
            const applicationId = this.application.applicationId;

            this.applicationNoteService.get(['Application', applicationId], new Map()).subscribe(
                x => {
                    this.applicationNotes = x;
                    this.loadingNotes = false;
                    this.refreshApplicatioNoteTable();
                },
                (_) => {
                    this.loadingNotes = false;
                }
            );
        }
    }

    deleteNote(applicationNoteId: string): void {
        if (this.application?.applicationId != null) {
            this.applicationNoteService.delete(applicationNoteId)
            .subscribe(_ => {
                this.getNotes();
            });
        }
    }

    refreshApplicatioNoteTable(): void {
        this.applicationNoteDataSource.data = this.applicationNotes;
    }

    goBack(): void {
        this.router.navigate(['application']);
    }
}
